/**
 * Parâmetros da org: canal/anti-ban (`save`) e destino (`saveDestination`).
 */
import { useCallback, useEffect, useState } from 'react';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { notify } from '../../../../utils/toast';
import * as followUpService from '../../../../services/modules/followUpService';
import {
    DEFAULT_BUSINESS_HOURS_END,
    DEFAULT_BUSINESS_HOURS_START,
    DEFAULT_FOLLOW_UP_TIMEZONE,
    DEFAULT_MAX_SENDS_PER_CYCLE,
    DEFAULT_MIN_INTERVAL_SECONDS,
    DEFAULT_PAUSE_TIMEOUT_HOURS,
    PAUSE_TIMEOUT_HOURS_MIN,
    isAllowedFollowUpTimezone,
    isBusinessHoursRangeValid,
    sliceTimeToHHmm,
    snapMaxSendsPerCycle,
    snapMinIntervalSeconds,
} from '../followUp.constants';
import type { FollowUpSettings } from '../types';

type Draft = Omit<FollowUpSettings, 'organizationId' | 'pauseTimeoutHours'> & {
    pauseTimeoutHours: number | '';
};

const INITIAL_DRAFT: Draft = {
    backupConnectionIds: [],
    minIntervalSeconds: DEFAULT_MIN_INTERVAL_SECONDS,
    maxSendsPerCycle: DEFAULT_MAX_SENDS_PER_CYCLE,
    pauseTimeoutHours: '',
    timezone: DEFAULT_FOLLOW_UP_TIMEZONE,
    businessHoursStart: DEFAULT_BUSINESS_HOURS_START,
    businessHoursEnd: DEFAULT_BUSINESS_HOURS_END,
    businessHoursWeekdaysOnly: true,
    stageIds: [],
};

function toDraft({ organizationId: _org, ...rest }: FollowUpSettings): Draft {
    return {
        ...rest,
        minIntervalSeconds: snapMinIntervalSeconds(rest.minIntervalSeconds),
        maxSendsPerCycle: snapMaxSendsPerCycle(rest.maxSendsPerCycle),
    };
}

export function useFollowUpSettings() {
    const organizationId = useOrganization().currentOrganization?.id;
    const [draft, setDraft] = useState<Draft>(INITIAL_DRAFT);
    const [snapshot, setSnapshot] = useState<Draft | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    useEffect(() => {
        if (!organizationId) return;
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        void followUpService.getSettings(organizationId)
            .then((data) => {
                if (cancelled) return;
                const next = toDraft(data);
                setDraft(next);
                setSnapshot(next);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('[useFollowUpSettings] Erro ao carregar parâmetros:', error);
                setSnapshot(null);
                setLoadError('Erro ao carregar os parâmetros de follow-up.');
                notify.error('Erro ao carregar os parâmetros de follow-up.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [organizationId, reloadKey]);

    const pauseTimeout = draft.pauseTimeoutHours === ''
        ? DEFAULT_PAUSE_TIMEOUT_HOURS
        : draft.pauseTimeoutHours;
    const pauseOk = pauseTimeout >= PAUSE_TIMEOUT_HOURS_MIN;
    const hoursOk =
        isAllowedFollowUpTimezone(draft.timezone) &&
        isBusinessHoursRangeValid(draft.businessHoursStart, draft.businessHoursEnd);
    const isLoaded = snapshot !== null;
    const isDestinationDirty = isLoaded && (
        draft.timezone !== snapshot.timezone ||
        draft.businessHoursStart !== snapshot.businessHoursStart ||
        draft.businessHoursEnd !== snapshot.businessHoursEnd ||
        draft.businessHoursWeekdaysOnly !== snapshot.businessHoursWeekdaysOnly ||
        JSON.stringify(draft.stageIds) !== JSON.stringify(snapshot.stageIds)
    );

    const persist = async (
        patch: Partial<Omit<FollowUpSettings, 'organizationId'>>,
        errorMessage: string,
        successMessage?: string,
    ) => {
        if (!organizationId || !isLoaded) return;
        setSaving(true);
        try {
            const next = toDraft(await followUpService.upsertSettings(organizationId, patch));
            setDraft(next);
            setSnapshot(next);
            setLoadError(null);
            if (successMessage) notify.success(successMessage);
        } catch (error) {
            console.error(errorMessage, error);
            notify.error(errorMessage);
            throw error;
        } finally {
            setSaving(false);
        }
    };

    const save = async () => {
        if (!organizationId || !isLoaded) return;
        if (!pauseOk) {
            notify.error(`Pausa após resposta humana deve ser no mínimo ${PAUSE_TIMEOUT_HOURS_MIN}.`);
            return;
        }
        await persist(
            {
                backupConnectionIds: draft.backupConnectionIds,
                minIntervalSeconds: snapMinIntervalSeconds(draft.minIntervalSeconds),
                maxSendsPerCycle: snapMaxSendsPerCycle(draft.maxSendsPerCycle),
                pauseTimeoutHours: pauseTimeout,
            },
            'Erro ao salvar os parâmetros de follow-up.',
            'Parâmetros salvos com sucesso!',
        );
    };

    const saveDestination = async () => {
        if (!organizationId || !isLoaded) return;
        if (!isAllowedFollowUpTimezone(draft.timezone)) {
            notify.error('Selecione um fuso horário válido.');
            throw new Error('Fuso horário inválido.');
        }
        if (!isBusinessHoursRangeValid(draft.businessHoursStart, draft.businessHoursEnd)) {
            notify.error('O horário de início deve ser anterior ao horário de fim.');
            throw new Error('Horário comercial inválido.');
        }
        await persist(
            {
                timezone: draft.timezone,
                businessHoursStart: sliceTimeToHHmm(draft.businessHoursStart),
                businessHoursEnd: sliceTimeToHHmm(draft.businessHoursEnd),
                businessHoursWeekdaysOnly: draft.businessHoursWeekdaysOnly,
                stageIds: draft.stageIds,
            },
            'Erro ao salvar o destino do follow-up.',
        );
    };

    const patch = <K extends keyof Draft>(key: K, value: Draft[K]) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
    };

    const patchIds = (key: 'backupConnectionIds' | 'stageIds', id: string, add: boolean) => {
        if (add && !id) return;
        setDraft((prev) => {
            const list = prev[key];
            return {
                ...prev,
                [key]: add
                    ? (list.includes(id) ? list : [...list, id])
                    : list.filter((item) => item !== id),
            };
        });
    };

    return {
        ...draft,
        addBackupConnection: (id: string) => patchIds('backupConnectionIds', id, true),
        removeBackupConnection: (id: string) => patchIds('backupConnectionIds', id, false),
        setMinIntervalSeconds: (value: number) => patch('minIntervalSeconds', value),
        setMaxSendsPerCycle: (value: number) => patch('maxSendsPerCycle', value),
        setPauseTimeoutHours: (value: number | '') => patch('pauseTimeoutHours', value),
        setTimezone: (value: string) => patch('timezone', value),
        setBusinessHoursStart: (value: string) => patch('businessHoursStart', value),
        setBusinessHoursEnd: (value: string) => patch('businessHoursEnd', value),
        setBusinessHoursWeekdaysOnly: (value: boolean) => patch('businessHoursWeekdaysOnly', value),
        addStageId: (id: string) => patchIds('stageIds', id, true),
        removeStageId: (id: string) => patchIds('stageIds', id, false),
        loading,
        saving,
        loadError,
        isLoaded,
        isDestinationDirty,
        canSave: isLoaded && pauseOk && !saving,
        canSaveDestination: isLoaded && hoursOk && !saving,
        save,
        saveDestination,
        reload,
    };
}
